// useFriends — the band-scoped Friends bridge hook, mirroring
// useTeams: built on useBandQuery/useBandMutation so the query key is ALWAYS ['band', bandId, 'friends'] (the
// tenant in the key, the structural fix). Friends are the ONE artist↔artist bridge (
// RULE ISOLATION) — opt-in, symmetric, keyed to the exact root-account email + an accept handshake.
//
// The friend-profile browse modal is the PASSIVE channel: it renders ONLY what FriendProfileRead carries
// (header/members/friends/upcomingShows) — no finances/contacts/ops/merch, which the DTO structurally cannot
// carry. Every mutation surfaces the server error verbatim and NEVER reports success on an
// unpersisted write. All friends routes are `friends:manage`; the browse profile requires an accepted
// friendship, so a 404 (unfriended under us) surfaces as the query's error state.
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext, useBandContextOrNull } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type FriendsListRead = components["schemas"]["FriendsListRead"];
type FriendSummary = components["schemas"]["FriendSummary"];
type FriendLookupResult = components["schemas"]["FriendLookupResult"];
type FriendRequestResult = components["schemas"]["FriendRequestResult"];
type FriendProfileRead = components["schemas"]["FriendProfileRead"];
type FriendProfileHeader = components["schemas"]["FriendProfileHeader"];
type FriendProfileMember = components["schemas"]["FriendProfileMember"];
type FriendProfileFriend = components["schemas"]["FriendProfileFriend"];
type FriendProfileShow = components["schemas"]["FriendProfileShow"];

export type {
  FriendsListRead,
  FriendSummary,
  FriendLookupResult,
  FriendRequestResult,
  FriendProfileRead,
  FriendProfileHeader,
  FriendProfileMember,
  FriendProfileFriend,
  FriendProfileShow,
};

/** The accept/decline action a recipient takes on an incoming request. Presentation vocabulary only. */
export type RespondAction = "accept" | "decline";

/** Up to two initials from a band name for the avatar fallback (born-blank → "?"). Media→URL is Phase 4. */
export function bandInitials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  return initials || "?";
}

/** A member's full name, or a designed placeholder when both parts are blank. */
export function memberName(m: Pick<FriendProfileMember, "firstName" | "lastName">): string {
  return [m.firstName, m.lastName].filter(Boolean).join(" ").trim() || "Unnamed member";
}

/** Two initials for a member avatar fallback (born-blank → "?"). */
export function memberInitials(m: Pick<FriendProfileMember, "firstName" | "lastName">): string {
  const s = `${m.firstName?.trim()?.[0] ?? ""}${m.lastName?.trim()?.[0] ?? ""}`.toUpperCase();
  return s || "?";
}

const friendsKey = (bandId: string) => ["band", bandId, "friends"] as const;

interface FriendsSnapshot {
  previous?: FriendsListRead;
}

/** Restore the friends cache to its pre-mutation snapshot (or refetch when there was none). */
function rollback(qc: QueryClient, key: readonly unknown[], previous: FriendsListRead | undefined): void {
  if (previous) qc.setQueryData(key, previous);
  else void qc.invalidateQueries({ queryKey: key });
}

// ---- queries ------------------------------------------------------------------------------------------

/**
 * The band ids of the ACTIVE friend bands, as a set — or an empty set when no band is mounted.
 *
 * Context-SAFE, the {@link useBandTeamOrNull} pattern: `useFriends` runs on `useBandQuery` and so uses the
 * THROWING band context, which a surface that also renders under the roster/org shell cannot call. This runs
 * the identical query under the identical key, so it shares one cache entry with `useFriends` and costs no
 * extra request where the friends list is already loaded.
 *
 * An empty set outside a band shell is the CORRECT answer rather than a degrade: a friend band is a
 * relationship between two BANDS, and there is no band here to be one end of it.
 */
export function useFriendBandIds(): ReadonlySet<string> {
  const { api } = useApp();
  const bandId = useBandContextOrNull()?.bandId;
  const q = useQuery<FriendsListRead, Error>({
    queryKey: ["band", bandId, "friends"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/friends", {
        params: { path: { bandId: bandId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 30_000,
    enabled: !!bandId,
  });
  return new Set((q.data?.active ?? []).map((f) => f.friendBandId).filter(Boolean) as string[]);
}

/**
 * `GET /bands/{bandId}/friends` (friends:manage) — the band's friendships split into active / incoming /
 * outgoing. A 403 (no friends:manage) surfaces as the query error; the card is gated so it never mounts then.
 */
export function useFriends() {
  const { api } = useApp();
  return useBandQuery<FriendsListRead>(
    ["friends"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/friends", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/**
 * The band-profile VIEW read, source picked by target: the caller's OWN band rides
 * `GET /bands/{bandId}/profile` (band:read — every seat may look at its own band's face), any other band
 * rides the friend browse `GET /bands/{bandId}/friends/{friendBandId}/profile` (friends:manage; 404 unless
 * an ACTIVE friendship exists — that 404 surfaces as the modal's "not available" state). One shape
 * (`FriendProfileRead`) either way, so the modal never cares which door served it.
 *
 * NOT `useBandProfile` (useBandProfile.ts), which is the Artist Profile SETTINGS tab's read-write pair —
 * this is the display face, that is the form.
 */
export function useBandProfileView(targetBandId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<FriendProfileRead>(
    ["bandProfile", targetBandId ?? "none"],
    async (bandId) => {
      const { data, error, response } =
        targetBandId === bandId
          ? await api.GET("/api/v1/bands/{bandId}/profile", { params: { path: { bandId } } })
          : await api.GET("/api/v1/bands/{bandId}/friends/{friendBandId}/profile", {
              params: { path: { bandId, friendBandId: targetBandId as string } },
            });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: targetBandId != null && targetBandId !== "", staleTime: 30_000 },
  );
}

// ---- mutations ----------------------------------------------------------------------------------------

/**
 * `POST /bands/{bandId}/friends/lookup` ({email}) → FriendLookupResult — the account-photo confirm step of the
 * add-friend handshake ("the matching account photo pops up to confirm it's right"). A 404
 * (no band on that root email) throws ApiError(404); the modal renders the designed "not found" state. Not
 * optimistic (a pure lookup).
 */
export function useFriendLookup() {
  const { api } = useApp();
  return useBandMutation<FriendLookupResult, string>(async (bandId, email) => {
    const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/friends/lookup", {
      params: { path: { bandId } },
      body: { email },
    });
    if (!response.ok || !data) throw new ApiError(response.status, error);
    return data;
  });
}

/**
 * `POST /bands/{bandId}/friends/requests` ({email}) → 201 FriendRequestResult. Invalidates the friends key so
 * the new outgoing-pending row appears. The guard errors (400 bad email, 403 not-AV, 409 already-friends /
 * ambiguous-owner / self) are surfaced INLINE by the modal (it branches on the ApiError status) — this hook
 * does NOT toast, so there's no double-report over the modal's tailored message.
 */
export function useSendFriendRequest() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = friendsKey(bandId);

  return useBandMutation<FriendRequestResult, string>(
    async (band, email) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/friends/requests", {
        params: { path: { bandId: band } },
        body: { email },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/**
 * `POST /bands/{bandId}/friends/requests/{friendshipId}/respond` ({action}) → 204. The first response clears
 * the request for everyone. Optimistic: remove the row from `incoming` immediately (accept
 * AND decline both leave the incoming list); the onSettled invalidate re-fetches, so an accept re-appears in
 * `active`. Rollback + toast on error.
 */
export function useRespondFriendRequest() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = friendsKey(bandId);

  return useBandMutation<void, { friendshipId: string; action: RespondAction }, FriendsSnapshot>(
    async (band, { friendshipId, action }) => {
      const { error, response } = await api.POST(
        "/api/v1/bands/{bandId}/friends/requests/{friendshipId}/respond",
        { params: { path: { bandId: band, friendshipId } }, body: { action } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async ({ friendshipId }) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<FriendsListRead>(key);
        qc.setQueryData<FriendsListRead>(key, (old) =>
          old ? { ...old, incoming: (old.incoming ?? []).filter((f) => f.friendshipId !== friendshipId) } : old,
        );
        return { previous };
      },
      onError: (err, _v, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't respond to the request"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/**
 * `DELETE /bands/{bandId}/friends/{friendBandId}` → 204. Unfriends an ACTIVE band (stops FUTURE sharing but
 * keeps past data, rule 14) AND revokes an OUTGOING PENDING request — one endpoint, because the gate's
 * remove_friend has no status guard, so the caller-side action is the same DELETE either way. Optimistic
 * remove from BOTH `active` and `outgoing` (the revoke case) with rollback + toast.
 */
export function useUnfriend() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = friendsKey(bandId);

  return useBandMutation<void, string, FriendsSnapshot>(
    async (band, friendBandId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/friends/{friendBandId}", {
        params: { path: { bandId: band, friendBandId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async (friendBandId) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<FriendsListRead>(key);
        qc.setQueryData<FriendsListRead>(key, (old) =>
          old
            ? {
                ...old,
                active: (old.active ?? []).filter((f) => f.friendBandId !== friendBandId),
                outgoing: (old.outgoing ?? []).filter((f) => f.friendBandId !== friendBandId),
              }
            : old,
        );
        return { previous };
      },
      onError: (err, _id, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't unfriend"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}
